import { useCartBooking } from '@/hooks/useCartBooking';
import React, { useMemo, useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { formatApiError, mediaUrl } from '@/lib/api';
import { MapPin, Minus, Plus, X, ConciergeBell, Store, Wallet } from 'lucide-react';
import { rentalsService, rentalCategoryLabel } from '@/services/rentals.service';
const euro = n => `€${Number(n || 0).toLocaleString('it-IT', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})}`;
const SERVICE_FEE = 1;
// yyyy-mm-dd di oggi / domani (per default date per_day)
const isoDay = (offset = 0) => {
  const dte = new Date();
  dte.setDate(dte.getDate() + offset);
  return dte.toISOString().slice(0, 10);
};
function Stepper({
  value,
  onChange,
  min = 1,
  max = 10
}) {
  return <div className="inline-flex items-center rounded-full border border-border bg-card" data-testid="rental-quantity-stepper">
      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-full" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} aria-label="Diminuisci">
        <Minus className="h-4 w-4" />
      </Button>
      <span className="w-8 text-center text-sm font-semibold tabular-nums" data-testid="rental-quantity-value">{value}</span>
      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-full" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} aria-label="Aumenta">
        <Plus className="h-4 w-4" />
      </Button>
    </div>;
}
export function RentalBookingDialog({
  service,
  open,
  onClose
}) {
  const addToUnifiedCart = useCartBooking();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qty, setQty] = useState(1);
  const [startDate, setStartDate] = useState(isoDay(1));
  const [endDate, setEndDate] = useState(isoDay(2));
  const [terms, setTerms] = useState(false);
  const [creatingIntent, setCreatingIntent] = useState(false);
  useEffect(() => {
    let active = true;
    if (service && open) {
      setQty(1);
      setTerms(false);
      setCreatingIntent(false);
      setStartDate(isoDay(1));
      setEndDate(isoDay(2));
      setDetail(service);
      setLoading(true);
      rentalsService.publicGet(service.slug || service.id).then(d => {
        if (active) setDetail(d);
      }).catch(() => {/* usa i dati di lista */}).finally(() => {
        if (active) setLoading(false);
      });
    }
    return () => {
      active = false;
    };
  }, [service, open]);
  const svc = detail || service;
  const perDay = svc?.pricing_mode === 'per_day';
  const stock = svc?.stock || 8;
  const days = useMemo(() => {
    if (!perDay) return 1;
    const a = new Date(startDate);
    const b = new Date(endDate);
    const diff = Math.round((b - a) / 86400000);
    return diff > 0 ? diff : 0;
  }, [perDay, startDate, endDate]);
  const unit = svc?.price || 0;
  const subtotal = unit * qty * (perDay ? days : 1);
  const fees = SERVICE_FEE;
  const total = subtotal + fees;
  const deposit = (svc?.deposit || 0) * qty;
  if (!service) return null;
  const goCheckout = async () => {
    if (!terms) {
      toast.error('Devi accettare i termini per procedere');
      return;
    }
    if (creatingIntent) return;
    setCreatingIntent(true);
    try {
      if (await addToUnifiedCart('rental', {
        service_id: svc.id,
        qty,
        ...(perDay ? {
          start_date: startDate,
          end_date: endDate
        } : {})
      })) onClose();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || 'Impossibile aggiungere al carrello');
    } finally {
      setCreatingIntent(false);
    }
  };
  const cover = svc?.cover || svc?.cover_image || (svc?.images || [])[0] || '';
  const catLabel = svc?.category_label || rentalCategoryLabel(svc?.category);
  return <Dialog open={open} onOpenChange={o => {
    if (!o) onClose();
  }}>
      <DialogContent className="max-h-[92vh] gap-0 overflow-hidden rounded-2xl border-border p-0 sm:max-w-lg" data-testid="rental-detail-dialog">
        <div className="relative h-40 w-full overflow-hidden bg-muted sm:h-48">
          {cover ? <img src={mediaUrl(cover)} alt={svc?.title} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center"><ConciergeBell className="h-10 w-10 text-muted-foreground" /></div>}
          <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.55) 100%)'
        }} />
          <button type="button" onClick={onClose} className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur transition-colors hover:bg-black/60" aria-label="Chiudi" data-testid="rental-detail-close">
            <X className="h-4 w-4" />
          </button>
          <span className="absolute left-3 top-3 inline-flex items-center rounded-full bg-primary/90 px-2.5 py-1 text-xs font-semibold text-primary-foreground backdrop-blur">{catLabel}</span>
          <div className="absolute inset-x-4 bottom-3">
            <h2 className="font-display text-xl font-bold leading-tight text-white drop-shadow sm:text-2xl" data-testid="rental-detail-title">{svc?.title}</h2>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-white/90">
              {svc?.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{svc.location}</span>}
              {svc?.owner_name && <span className="flex items-center gap-1"><Store className="h-3.5 w-3.5" />{svc.owner_name}</span>}
            </p>
          </div>
        </div>

        <ScrollArea className="max-h-[calc(92vh-12rem)]">
          <div className="p-5">
            <div className="space-y-5" data-testid="rental-select-step">
                {loading ? <Skeleton className="h-16 w-full rounded-xl" /> : <p className="text-sm leading-relaxed text-muted-foreground">{svc?.description || svc?.short}</p>}

                <div className="flex items-center justify-between rounded-xl border border-border bg-muted/40 px-3.5 py-2.5">
                  <span className="text-sm font-medium text-foreground">{perDay ? 'Prezzo al giorno' : 'Prezzo del servizio'}</span>
                  <span className="text-lg font-bold tabular-nums text-foreground">{euro(unit)}{perDay ? <span className="text-xs font-normal text-muted-foreground"> /giorno</span> : null}</span>
                </div>

                {perDay && <div className="grid grid-cols-2 gap-3" data-testid="rental-date-range">
                    <div>
                      <Label htmlFor="rn-start" className="mb-1.5 block text-sm">Ritiro</Label>
                      <Input id="rn-start" type="date" min={isoDay(0)} value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="rental-start-date" />
                    </div>
                    <div>
                      <Label htmlFor="rn-end" className="mb-1.5 block text-sm">Riconsegna</Label>
                      <Input id="rn-end" type="date" min={startDate} value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="rental-end-date" />
                    </div>
                  </div>}

                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-foreground">Quantità {stock ? <span className="font-normal text-muted-foreground">(max {stock})</span> : null}</Label>
                  <Stepper value={qty} onChange={setQty} max={stock} />
                </div>

                <Separator />

                <div className="space-y-1.5" data-testid="rental-price-summary">
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>{euro(unit)} × {qty}{perDay ? ` × ${days} giorn${days === 1 ? 'o' : 'i'}` : ''}</span>
                    <span className="tabular-nums">{euro(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground"><span>Commissione di servizio</span><span className="tabular-nums">{euro(fees)}</span></div>
                  <div className="flex justify-between pt-1 text-base font-bold text-foreground"><span>Totale</span><span className="tabular-nums" data-testid="rental-total">{euro(total)}</span></div>
                  {deposit > 0 && <div className="mt-2 flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="rental-deposit-note">
                      <Wallet className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>Cauzione di <strong className="text-foreground">{euro(deposit)}</strong> da versare al ritiro (rimborsabile alla riconsegna).</span>
                    </div>}
                </div>

                <label className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Checkbox checked={terms} onCheckedChange={v => setTerms(!!v)} data-testid="cart-selection-terms" />
                  <span>Accetto i termini del servizio e la politica di rimborso.</span>
                </label>
                <Button className="h-11 w-full rounded-full text-sm font-semibold" onClick={goCheckout} disabled={perDay && days <= 0} data-testid="rental-book-button">
                  <ConciergeBell className="mr-2 h-4 w-4" /> Aggiungi al carrello · {euro(total)}
                </Button>
              </div>

            

            
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>;
}
export default RentalBookingDialog;
